import * as React from 'react';
import { buildAdapter } from './grouped-table/helper';
import { Configure, Index, InstantSearch, ClearRefinements } from 'react-instantsearch';
import NvChartHitsWidget from './nv-chart/partials/NvChartHitsWidget';
import Filter from './nv-chart/partials/Filter';
import { chartStyles, Config, filtersConfig, futureFlags, typesenseClientConfig } from './nv-chart/helpers';
import Preloader from './nv-chart/partials/Preloader';
import { FilterButton } from './faceted-search/react/helper';
import Offcanvas from './faceted-search/react/Offcanvas';
import OffcanvasContent from './nv-chart/partials/OffcanvasContent';
import GroupDropdown from './nv-chart/partials/GroupDropdown';
import GroupLabel from './nv-chart/partials/GroupLabel';
import { useFacetBootstrap } from './nv-chart/api';

const getInitialGroupFromUrl = (groups) => {
  const urlParams = new URLSearchParams(window.location.search);
  const requestedGroup = urlParams.get('group');
  const matchedGroup = groups.find(group => group.label.toLowerCase() === requestedGroup?.toLowerCase());
  const defaultGroup = groups[0];

  return matchedGroup?.label ?? defaultGroup.label;
};

const syncGroupToUrl = (groupLabel) => {
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.set('group', groupLabel);
  window.history.replaceState({}, '', currentUrl);
};

export default function NvChart(props) {
  const {settings} = props;
  let {defaultIndex} = settings;
  const {
    title,
    style,
    lineStyle,
    groups = []
  } = settings;

  if (groups.length === 0) {
    return (
      <Preloader title='Chart'/>
    );
  }

  const previewIndex = React.useMemo(
    () => document.getElementById('library-preview-token')?.dataset?.index,
    []
  );
  if (previewIndex) {
    defaultIndex = previewIndex;
  }

  const [groupLabel, setGroupLabel] = React.useState(() => getInitialGroupFromUrl(groups));

  const handleGroupChange = React.useCallback((newLabel) => {
    setGroupLabel(newLabel);
    syncGroupToUrl(newLabel);
  }, []);
  const css = chartStyles(style);
  const adapter = React.useMemo(() => buildAdapter(defaultIndex, typesenseClientConfig), [defaultIndex]);

  const currentGroup = React.useMemo(
    () => groups.find(g => g.label === groupLabel),
    [groups, groupLabel]
  );

  const facets = currentGroup.filters ? currentGroup.filters.map(item => item.attribute).join(',') : '';
  const bootstrapConfig = currentGroup.label ? {
    adapter,
    facets,
    index: defaultIndex,
    filters: currentGroup.filters,
    filterBy: currentGroup.filterBy,
    groupByFields: currentGroup.groupByFields,
  } : {};
  const {loading, initialUiState, facetCounts} = useFacetBootstrap(bootstrapConfig);

  const ref = React.useRef(null);
  if (loading || !initialUiState) {
    return <Preloader title='Chart'/>;
  }

  return (
    <div className='nv-chart-groups'>
      <div className='nv-chart-group' key={currentGroup.label} ref={ref}>
        <InstantSearch
          searchClient={adapter.searchClient}
          indexName={defaultIndex}
          {...(initialUiState) ? {initialUiState: initialUiState} : {}}
          future={futureFlags()}>
          <Configure hitsPerPage={250} {...(filtersConfig(currentGroup) ? {filters: filtersConfig(currentGroup)} : {})}/>
          <div className={css.root}>
            <Offcanvas title={'Filters'} callback={() => {
            }}>
              <OffcanvasContent>
                <div className='nv-chart__filters'>
                  <GroupDropdown groupLabel={groupLabel} groups={groups} valueSetter={handleGroupChange}/>
                  {currentGroup.filters.length > 0 && currentGroup.filters.map((filter, filterIndex) => {
                    return <Filter group={currentGroup} key={filter.attribute} config={filter} facetCounts={facetCounts} isFirstFacet={filterIndex === 0}/>
                  })}
                </div>
              </OffcanvasContent>
            </Offcanvas>
            <div className='container'>
              <div className='row'>
                <div className='col-lg-3 d-none d-lg-block d-xl-block d-xxl-block'>
                  <div className='nv-chart__filters desktop-filters'>
                    <div className='d-none clear-group-filters'>
                      <ClearRefinements/>
                    </div>
                  <GroupDropdown groupLabel={groupLabel} groups={groups} valueSetter={handleGroupChange}/>
                  <h3 className='h--smaller my-0 nv-chart__filters-heading'>{Config.I18n.Filters.Title}</h3>
                  {currentGroup.filters.length > 0 && currentGroup.filters.map((filter, filterIndex) => {
                    return <Filter key={filter.attribute} group={currentGroup} config={filter} facetCounts={facetCounts} isFirstFacet={filterIndex === 0}/>;
                  })}
                  </div>
                </div>
                <div className='col-lg-9'>
                  <div className='nv-chart__header'>
                    <h3 className='h--small my-0'>{title}</h3>
                    <FilterButton/>
                  </div>
                  <div className='nv-chart__view'>
                    <div className='text-center mb-3'>
                      <div className='nv-chart__group-description'>
                        <h3 className='h--smallest'>{currentGroup.description}</h3>
                      </div>
                      <div className='nv-chart__group-label'>
                        <GroupLabel currentGroup={currentGroup}/>
                      </div>
                    </div>
                    <NvChartHitsWidget chartConfig={currentGroup} lineStyle={lineStyle}/>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </InstantSearch>
      </div>
    </div>
  );
}
